import dayjs from 'dayjs';

import { getAvailabilityDates, validateExpiryDate, validateDateInput, validateMaxDate } from './entitlements-date-validation';
import type { FeedEntitlementRow, InventoryFeedRow, ValidationErrors } from '../entitlements-types';

type ValidateDateInputArgs = {
  value: string;
  availableStartDate: Date;
  availableEndDate: Date;
  isDayEntitlement: boolean;
  oppositeDate: string;
  key: 'startDate' | 'endDate';
  missingDays: string[];
  missingMonths: string[];
  sameFeeds: FeedEntitlementRow[];
};

const typedValidateDateInput = validateDateInput as unknown as (args: ValidateDateInputArgs) => string | undefined;

export function buildValidationErrors(
  feedInventory: InventoryFeedRow[] = [],
  draftEntitlements: FeedEntitlementRow[] = []
): ValidationErrors {
  if (!feedInventory.length) {
    return {};
  }

  const getGenericDate = {
    startDate: () => dayjs('2000-01-01').format('YYYY-MM-DD'),
    endDate: () => dayjs().format('YYYY-MM-DD'),
  };

  return draftEntitlements.reduce((obj, currentEntitlement) => {
    const sameFeeds: FeedEntitlementRow[] = draftEntitlements.filter(
      (candidateEntitlement) =>
        candidateEntitlement.id !== currentEntitlement.id && candidateEntitlement.feedId === currentEntitlement.feedId
    );
    const dataFeed = feedInventory.find((feed) => feed.id === currentEntitlement.feedId);
    if (!dataFeed) {
      return {
        ...obj,
        [currentEntitlement.id]: {},
      };
    }

    const availabilityStartDate = dataFeed.availabilityStartDate ?? getGenericDate.startDate();
    const availabilityEndDate = dataFeed.availabilityEndDate ?? getGenericDate.endDate();

    const dataFeedAvailability = {
      availabilityStartDate,
      availabilityEndDate,
      availabilityStartMonth: dataFeed.availabilityStartMonth ?? availabilityStartDate.slice(0, 7),
      availabilityEndMonth: dataFeed.availabilityEndMonth ?? availabilityEndDate.slice(0, 7),
      missingDays: dataFeed.missingDays ?? [],
      missingMonths: dataFeed.missingMonths ?? [],
    };

    const { availableStartDate, availableEndDate, missingDays, missingMonths } = getAvailabilityDates(
      dataFeedAvailability,
      currentEntitlement.isDayEntitlement
    );

    const generalizedValidateDateInput = (key: 'startDate' | 'endDate', value: string) => {
      const oppositeDateKey = key === 'startDate' ? 'endDate' : 'startDate';
      const oppositeDateValue = currentEntitlement[oppositeDateKey] ?? getGenericDate[oppositeDateKey]();

      return typedValidateDateInput({
        value,
        availableStartDate,
        availableEndDate,
        isDayEntitlement: currentEntitlement.isDayEntitlement,
        oppositeDate: oppositeDateValue,
        key,
        missingDays,
        missingMonths,
        sameFeeds,
      });
    };

    const endDate = currentEntitlement.endDate ?? getGenericDate.endDate();

    return {
      ...obj,
      [currentEntitlement.id]: {
        startDate: generalizedValidateDateInput('startDate', currentEntitlement.startDate ?? getGenericDate.startDate()),
        endDate: generalizedValidateDateInput('endDate', endDate),
        expiryDate: currentEntitlement.expiryDate ? validateExpiryDate(currentEntitlement.expiryDate) : undefined,
        onGoingMaxDate: currentEntitlement.onGoingMaxDate ? validateMaxDate(currentEntitlement.onGoingMaxDate, endDate) : undefined,
      },
    };
  }, {});
}
